import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import VideoCard from "../../components/VideoCard";
import { getSongs } from "../services/songService";
import {
  FiThumbsUp,
  FiMessageCircle,
  FiShare2,
} from "react-icons/fi";

import AboutSection from "../../components/home/AboutSection";
import HowItWorks from "../../components/home/HowItWorks";
import WhyChooseUs from "../../components/home/WhyChooseUs";
import CategoriesIntro from "../../components/home/CategoriesIntro";
import FAQSection from "../../components/home/FAQSection";

import "./home.css";

/* ===== UTILS ===== */
const shuffle = (arr = []) => [...arr].sort(() => Math.random() - 0.5);

export default function Home() {
  const navigate = useNavigate();

  /* ===== LOGIN CHECK ===== */
  const storedUser =
    JSON.parse(sessionStorage.getItem("user")) ||
    JSON.parse(localStorage.getItem("user"));

  const isLoggedIn = !!storedUser;

  const [videos, setVideos] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [songs, setSongs] = useState([]);

  /* ===== RECOMMENDED ===== */
  const [recommended, setRecommended] = useState([]);
  const [recPage, setRecPage] = useState(1);
  const [recLoading, setRecLoading] = useState(false);
  const recRef = useRef(null);

  /* ===== INITIAL LOAD ===== */
  useEffect(() => {
    const loadHome = async () => {
      try {
        const [vRes, sRes, pRes, songRes] = await Promise.all([
          api.get("/client/getVideos?start=1&limit=20"),
          api.get("/client/getShorts?start=1&limit=20"),
          api.get("/client/posts/feed?limit=20"),
          getSongs(1, 20),
        ]);

        setVideos(shuffle(vRes?.data?.videos || []));
        setShorts(shuffle(sRes?.data?.shorts || []));
        setPosts(pRes?.data?.status ? pRes.data.data || [] : []);

        if (songRes?.status) {
          setSongs(shuffle(songRes.songs || []));
        }
      } catch (err) {
        console.error("Home load error:", err);
      }
    };

    loadHome();
  }, []);

  /* ===== LOAD RECOMMENDED ===== */
  const loadRecommended = async () => {
    if (recLoading) return;
    setRecLoading(true);

    try {
      const res = await api.get("/client/getVideos", {
        params: { start: recPage, limit: 12 },
      });

      const list = shuffle(res?.data?.videos || []);
      setRecommended((prev) => {
        const ids = new Set(prev.map((v) => v._id));
        return [...prev, ...list.filter((v) => !ids.has(v._id))];
      });

      setRecPage((p) => p + 1);
    } catch (err) {
      console.error("Recommended load error:", err);
    } finally {
      setRecLoading(false);
    }
  };

  useEffect(() => {
    loadRecommended();
  }, []);

  /* ===== OBSERVER (SAFE VERSION) ===== */
  useEffect(() => {
    const currentRef = recRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadRecommended();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [recLoading]);

  return (
    <>
      <Helmet>
        <title>
          ShareVideo - Watch Videos, Shorts, Music & Photo Posts
        </title>
        <meta
          name="description"
          content="ShareVideo is a multi-format content platform where you can watch and upload videos, shorts, music, audio songs, and photo posts."
        />
        <link rel="canonical" href="https://sharevideo.in/" />
      </Helmet>

      {/* ===== HERO ===== */}
      {!isLoggedIn ? (
        <section className="hero">
          <h1>
            Watch. Create. <span>Earn.</span>
          </h1>
          <p>
            Upload videos, shorts, music, audio songs, and photo posts �
            grow your audience and earn through impressions.
          </p>
          <div className="hero-actions">
            <button onClick={() => navigate("/register")}>
              Get Started
            </button>
            <button
              className="outline"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
          </div>
        </section>
      ) : (
        <section className="hero">
          <h1>
            Welcome back,{" "}
            <span>
              {storedUser?.fullName ||
                storedUser?.nickName ||
                "Creator"}
            </span>
          </h1>
          <p>
            Ready to upload your next video, short, music, or photo post?
          </p>
          <div className="hero-actions">
            <button onClick={() => navigate("/user/upload-video")}>
              Upload Video
            </button>
            <button
              className="outline"
              onClick={() => navigate("/user/upload-short")}
            >
              Upload Short
            </button>
          </div>
        </section>
      )}

      <AboutSection />
      <HowItWorks />
      <WhyChooseUs />
      <CategoriesIntro />

      {/* ===== VIDEOS ===== */}
      <Section title="Videos" onViewAll={() => navigate("/videos")}>
        <div className="h-scroll">
          {videos.map((v) => (
            <VideoCard
              key={v._id}
              video={v}
              onClick={() => navigate(`/video/${v._id}`)}
            />
          ))}
        </div>
      </Section>

      {/* ===== SHORTS ===== */}
      <Section title="Shorts" onViewAll={() => navigate("/shorts")}>
        <div className="h-scroll">
          {shorts.map((s) => (
            <div
              key={s._id}
              className="short-card"
              onClick={() => navigate(`/shorts/${s._id}`)}
            >
              <img src={s.videoImage} alt="short" />
            </div>
          ))}
        </div>
      </Section>

      {/* ===== SONGS ===== */}
      <Section title="Songs" onViewAll={() => navigate("/songs")}>
        <div className="h-scroll">
          {songs.map((s) => (
            <div
              key={s.slug}
              className="card"
              onClick={() => navigate(`/song/${s.slug}`)}
              style={{ cursor: "pointer", minWidth: "180px" }}
            >
              <img
                src={s.thumbnailUrl || "/default-song.png"}
                alt={s.title}
              />
              <p className="card-title">{s.title}</p>
              <div className="post-meta">
                <span>{s.totalPlays ?? 0} plays</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== POSTS ===== */}
      <Section title="Posts" onViewAll={() => navigate("/post")}>
        <div className="h-scroll">
          {posts.map((p) => (
            <div key={p._id} className="card post-card">
              {p.media?.url && <img src={p.media.url} alt="post" />}
              {p.caption && <p className="post-text">{p.caption}</p>}
              <div className="post-meta">
                <span>
                  <FiThumbsUp /> {p.likesCount ?? 0}
                </span>
                <span>
                  <FiMessageCircle /> {p.commentsCount ?? 0}
                </span>
                <span>
                  <FiShare2 /> {p.sharesCount ?? 0}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== RECOMMENDED ===== */}
      <section className="section">
        <h3>Recommended</h3>
        <div className="grid">
          {recommended.map((v) => (
            <VideoCard
              key={v._id}
              video={v}
              onClick={() => navigate(`/video/${v._id}`)}
            />
          ))}
        </div>
        <div ref={recRef} style={{ height: 1 }} />
        {recLoading && <p>Loading more...</p>}
      </section>

      <FAQSection />
    </>
  );
}

/* ===== SECTION COMPONENT ===== */
function Section({ title, onViewAll, children }) {
  return (
    <section className="section">
      <div className="section-header">
        <h3>{title}</h3>
        <span onClick={onViewAll}>View all</span>
      </div>
      {children}
    </section>
  );
}